import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { API } from '@/api'
import { ServerImageInfo } from '@/api/type-server'
import { Button, ButtonRadio, Divider, Loading, message, Pagination, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { ImageProvider, ImageStoreStatus, PENDING_CUSTOM_IMAGE_STATUSES } from '@/consts/image'
import { useStorageBillingMode } from '@/hooks/useStorageBillingMode'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { useUserStore } from '@/stores/user'
import { getErrorCodeMessage } from '@/utils/error'
import { formatBytes } from '@/utils/formatter'
import { fetchAllPages } from '@/utils/pagination'
import {
  getBillingSwitchCompletedAt,
  SWITCH_RESUMABLE_POLL_INTERVAL_MS,
  SWITCH_RESUMABLE_POLL_WINDOW_MS,
} from '@/utils/storage-billing'

import { CustomImageTable } from './components/CustomImageTable'
import { DeleteImageModal } from './components/DeleteImageModal'
import { RestoreImageModal } from './components/RestoreImageModal'
import { UploadCustomizeImageModal } from './components/UploadCustomizeImageModal'
import { UploadImageModal } from './components/UploadImageModal'
import { UploadStatusIndicator } from './components/UploadStatusIndicator'
import { imageUploaderClient, UploadProgress } from './image-upload-client'
import { generateTaskId, ImageRegistrationResult, UploadPausedError, UploadTask } from './types'

interface FilterItem {
  label: string
  value: string
  disabled?: boolean
}

const PAGE_SIZE = 10
const POLLING_INTERVAL = 30 * 1000 // 30 seconds

const MACHINE_CATEGORY = {
  CONTAINER: 1,
  VM: 2,
} as const

const IMAGE_FORMAT = {
  CONTAINER: 0,
  VM: 1,
} as const

export function CustomImagePage() {
  const { t } = useTranslation()
  const userStore = useUserStore()
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [imageList, setImageList] = useState<ServerImageInfo[]>([])
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deletingImageId, setDeletingImageId] = useState<number | null>(null)

  const { imageOnSpaceBilling, loading: billingLoading } = useStorageBillingMode()
  const [restoringImage, setRestoringImage] = useState<ServerImageInfo | null>(null)
  const [restoreLoading, setRestoreLoading] = useState(false)
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([])
  const [continuingTaskId, setContinuingTaskId] = useState<string | null>(null)
  const [continuingImageId, setContinuingImageId] = useState<number | null>(null)
  const [continuingStoreService, setContinuingStoreService] = useState<string | null>(null)
  const [expectedFileName, setExpectedFileName] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploaderInstances = useRef<Map<string, typeof imageUploaderClient>>(new Map())

  // Throttle speed display updates
  const lastUIUpdateRef = useRef<Map<string, number>>(new Map())
  const SPEED_UPDATE_INTERVAL = 5000 // 5 seconds

  const imageTypeFilters: FilterItem[] = useMemo(() => {
    const filters: FilterItem[] = [
      {
        label: t('customImage.filter.container'),
        value: 'container',
      },
      {
        label: t('customImage.filter.virtualMachine'),
        value: 'vm',
      },
    ]
    if (!imageOnSpaceBilling) {
      filters.push({
        label: t('customImage.filter.restorable'),
        value: 'restorable',
      })
    }
    return filters
  }, [t, imageOnSpaceBilling])
  const [filter, setFilter] = useState<FilterItem>({
    label: t('customImage.filter.container'),
    value: 'container',
  })

  const createPendingTasksFromImages = useCallback((images: any[]) => {
    return images
      .filter(
        (image) =>
          PENDING_CUSTOM_IMAGE_STATUSES.includes(image.storeStatus) &&
          !image.public &&
          image.provider !== ImageProvider.RegistryURL,
      )
      .map((image) => ({
        id: `pending_${image.id}`,
        name: (image.alias || image.name || '').toLowerCase(), // Convert to lowercase for consistency
        progress: 0,
        status: 'pending' as const,
        startTime: new Date(image.createdTime * 1000),
        imageId: image.id,
        storeService: image.storeService,
      }))
  }, [])

  const getImageList = useCallback(
    async (showLoading = true) => {
      if (billingLoading) return
      if (showLoading) setLoading(true)
      try {
        const fetchCategory = (machineCategory: number) =>
          fetchAllPages({
            fetchPage: (requestPage, requestPageSize) =>
              API.getImageList({
                page: requestPage,
                per_page: requestPageSize,
                machine_category: machineCategory,
                owned: true,
              }),
            getItems: (response) => response.images,
          })

        let ownedImages: ServerImageInfo[]
        if (imageOnSpaceBilling) {
          const serverImages = await fetchCategory(
            filter.value === 'container' ? MACHINE_CATEGORY.CONTAINER : MACHINE_CATEGORY.VM,
          )
          ownedImages =
            serverImages.filter(
              (image) =>
                !image.public &&
                image.format ===
                  (filter.value === 'container' ? IMAGE_FORMAT.CONTAINER : IMAGE_FORMAT.VM),
            ) || []
        } else {
          const [containerImages, vmImages] = await Promise.all([
            fetchCategory(MACHINE_CATEGORY.CONTAINER),
            fetchCategory(MACHINE_CATEGORY.VM),
          ])
          const mergedById = new Map<number, ServerImageInfo>()
          for (const image of [...containerImages, ...vmImages]) {
            if (!image.public) {
              mergedById.set(image.id, image)
            }
          }
          ownedImages = Array.from(mergedById.values())
        }
        setImageList(ownedImages)

        const pendingTasks = createPendingTasksFromImages(ownedImages)
        setUploadTasks((prev) => {
          const clientTasks = prev.filter((task) => !task.id.startsWith('pending_'))

          // Filter out pending tasks that already have a corresponding client task with the same imageId
          const existingImageIds = new Set(clientTasks.map((task) => task.imageId).filter(Boolean))
          const newPendingTasks = pendingTasks.filter((task) => !existingImageIds.has(task.imageId))

          return [...clientTasks, ...newPendingTasks]
        })
      } catch (error) {
        console.error('Failed to fetch image list:', error)
        message.error('Failed to load images. Please try again.')
        setImageList([])
      } finally {
        if (showLoading) setLoading(false)
      }
    },
    [filter, imageOnSpaceBilling, billingLoading, createPendingTasksFromImages],
  )

  const handleDeleteImage = useCallback((imageId: number) => {
    setDeletingImageId(imageId)
    setIsDeleteModalOpen(true)
  }, [])

  const confirmDeleteImage = useCallback(async (imageId: number) => {
    try {
      const { code } = await API.deleteImage({ id: imageId })
      if (code !== 0) {
        message.error('Failed to delete image. Please try again.')
        return
      }
      message.success('Image deleted successfully')
      // Remove from local state
      setImageList((prev) => prev.filter((image) => image.id !== imageId))
      // Remove related upload tasks
      setUploadTasks((prev) => prev.filter((task) => task.imageId !== imageId))
      setIsDeleteModalOpen(false)
      setDeletingImageId(null)
    } catch (error) {
      console.error('Failed to delete image:', error)
      message.error('Failed to delete image. Please try again.')
    }
  }, [])

  useEffect(() => {
    getImageList()
  }, [getImageList])

  const displayList = useMemo(() => {
    if (imageOnSpaceBilling) return imageList
    if (filter.value === 'restorable') {
      return imageList.filter((image) => image.storeStatus === ImageStoreStatus.Resumable)
    }
    const format = filter.value === 'container' ? IMAGE_FORMAT.CONTAINER : IMAGE_FORMAT.VM
    return imageList.filter(
      (image) => image.format === format && image.storeStatus !== ImageStoreStatus.Resumable,
    )
  }, [imageList, filter, imageOnSpaceBilling])

  const total = displayList.length

  const imagesStorageBytes = useMemo(
    () =>
      imageList
        .filter((image) => image.storeStatus !== ImageStoreStatus.Resumable)
        .reduce((sum, image) => sum + (image.size || 0), 0),
    [imageList],
  )

  const visibleImageList = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return displayList.slice(start, start + PAGE_SIZE)
  }, [displayList, page])

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))
    if (page > lastPage) setPage(lastPage)
  }, [page, total])

  // Auto-refresh list every 30 seconds (without loading indicator)
  useEffect(() => {
    const intervalId = setInterval(() => {
      getImageList(false)
    }, POLLING_INTERVAL)

    return () => clearInterval(intervalId)
  }, [getImageList])

  const hasResumableRef = useRef(false)
  hasResumableRef.current = imageList.some(
    (image) => image.storeStatus === ImageStoreStatus.Resumable,
  )

  useEffect(() => {
    const switchedAt = getBillingSwitchCompletedAt()
    if (!switchedAt || Date.now() - switchedAt >= SWITCH_RESUMABLE_POLL_WINDOW_MS) return

    const intervalId = setInterval(() => {
      if (Date.now() - switchedAt >= SWITCH_RESUMABLE_POLL_WINDOW_MS || hasResumableRef.current) {
        clearInterval(intervalId)
        return
      }
      getImageList(false)
    }, SWITCH_RESUMABLE_POLL_INTERVAL_MS)

    return () => clearInterval(intervalId)
  }, [getImageList])

  // Prevent page refresh during upload
  useEffect(() => {
    const hasActiveUploads = uploadTasks.some((task) =>
      ['starting', 'uploading', 'pausing'].includes(task.status),
    )

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [uploadTasks])

  const handleUploadStart = useCallback(
    (
      taskId: string,
      fileName: string,
      originalFile?: File,
      originalDescription?: string,
      imageId?: number,
      storeService?: string,
    ) => {
      const newTask: UploadTask = {
        id: taskId,
        name: fileName,
        progress: 0,
        status: 'starting',
        startTime: new Date(),
        originalFile,
        originalDescription,
        imageId,
        storeService,
      }
      setUploadTasks((prev) => [...prev, newTask])
    },
    [],
  )

  const handleUploadProgress = useCallback((taskId: string, progressData: UploadProgress) => {
    const now = Date.now()
    setUploadTasks((prev) =>
      prev.map((task) => {
        if (task.id !== taskId) return task
        if (task.status === 'paused') return task
        if (task.status === 'pausing') return { ...task, status: 'paused' as const }

        // Throttle UI updates (5s interval, or 100% complete)
        const lastUIUpdate = lastUIUpdateRef.current.get(taskId) || 0
        const isComplete = progressData.percentage >= 100
        const shouldUpdateUI = isComplete || now - lastUIUpdate >= SPEED_UPDATE_INTERVAL

        if (!shouldUpdateUI) {
          return task
        }

        // Calculate global average speed (stable, no fluctuation)
        const totalTime = (now - task.startTime.getTime()) / 1000
        const averageSpeed = totalTime > 0 ? progressData.loaded / totalTime : 0

        lastUIUpdateRef.current.set(taskId, now)
        return {
          ...task,
          progress: progressData.percentage,
          speed: averageSpeed,
          status: 'uploading' as const,
        }
      }),
    )
  }, [])

  const handleUploadComplete = useCallback(
    (_result: any, taskId: string) => {
      // Mark upload as completed
      setUploadTasks((prev) =>
        prev.map((task) =>
          task.id === taskId ? { ...task, progress: 100, status: 'completed' as const } : task,
        ),
      )

      // Remove completed task after 3 seconds
      setTimeout(() => {
        setUploadTasks((prev) => prev.filter((task) => task.id !== taskId))
      }, 3000)

      // Refresh image list when upload completes
      getImageList()
    },
    [getImageList],
  )

  const handleUploadError = useCallback((taskId: string, errorMessage?: string) => {
    setUploadTasks((prev) =>
      prev.map((task) => (task.id === taskId ? { ...task, status: 'error' as const } : task)),
    )
    if (errorMessage) {
      message.error(errorMessage)
    }
  }, [])

  const handleUploadPaused = useCallback((taskId: string) => {
    setUploadTasks((prev) =>
      prev.map((task) => (task.id === taskId ? { ...task, status: 'paused' as const } : task)),
    )
  }, [])

  const handleImageRegistered = useCallback(
    (registrationResult: ImageRegistrationResult) => {
      // Update the current upload task with imageId and storeService from registration result
      setUploadTasks((prev) =>
        prev.map((task) => {
          // Find the most recent upload task without imageId (the current one)
          if (task.status === 'starting' && !task.imageId) {
            return {
              ...task,
              imageId: registrationResult.imageId,
              storeService: registrationResult.storeServiceUrl,
            }
          }
          return task
        }),
      )

      // Refresh image list when image is registered
      getImageList()
    },
    [getImageList],
  )

  const handlePauseUpload = useCallback((taskId: string) => {
    const uploaderInstance = uploaderInstances.current.get(taskId)
    if (uploaderInstance) {
      uploaderInstance.pause()
      setUploadTasks((prev) =>
        prev.map((task) => (task.id === taskId ? { ...task, status: 'pausing' as const } : task)),
      )
    }
  }, [])

  const handleResumeUpload = useCallback(
    async (taskId: string) => {
      const uploaderInstance = uploaderInstances.current.get(taskId)
      if (uploaderInstance) {
        // Update status to uploading immediately
        setUploadTasks((prev) =>
          prev.map((task) =>
            task.id === taskId ? { ...task, status: 'uploading' as const } : task,
          ),
        )

        try {
          // Resume the upload with progress callback
          const result = await uploaderInstance.resumeUpload((progress) => {
            handleUploadProgress(taskId, progress)
          })

          // Mark as completed
          handleUploadComplete(result, taskId)
        } catch (error) {
          if (error instanceof UploadPausedError) {
            // Update status to paused when pause is triggered during resume
            setUploadTasks((prev) =>
              prev.map((task) =>
                task.id === taskId ? { ...task, status: 'paused' as const } : task,
              ),
            )
            return
          }

          const errorMessage =
            error instanceof Error ? error.message : 'Resume upload failed. Please try again.'
          handleUploadError(taskId, errorMessage)
        } finally {
          // Clean up uploader instance if upload completed or failed (but not if paused again)
          if (uploaderInstance && !uploaderInstance.isPaused()) {
            uploaderInstances.current.delete(taskId)
          }
        }
      }
    },
    [handleUploadComplete, handleUploadError, handleUploadProgress],
  )

  const handleRetryUpload = useCallback(
    async (taskId: string) => {
      const task = uploadTasks.find((t) => t.id === taskId)

      if (!task || !task.originalFile) {
        return
      }

      // Reset task status to starting
      setUploadTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: 'starting' as const, progress: 0 } : t)),
      )

      // Register uploader instance for this task
      uploaderInstances.current.set(taskId, imageUploaderClient)

      try {
        // Start full upload flow (registration + upload) using saved original file
        const result = await imageUploaderClient.uploadImage(
          task.originalFile,
          task.name,
          task.originalDescription || '',
          (progress) => {
            handleUploadProgress(taskId, progress)
          },
          (registrationResult) => {
            // Update task with imageId and storeService from registration result
            setUploadTasks((prev) =>
              prev.map((t) =>
                t.id === taskId
                  ? {
                      ...t,
                      imageId: registrationResult.imageId,
                      storeService: registrationResult.storeServiceUrl,
                    }
                  : t,
              ),
            )
            if (handleImageRegistered) {
              handleImageRegistered(registrationResult)
            }
          },
        )

        handleUploadComplete(result, taskId)
        uploaderInstances.current.delete(taskId)
      } catch (error) {
        if (error instanceof UploadPausedError) {
          // Update status to paused
          setUploadTasks((prev) =>
            prev.map((t) => (t.id === taskId ? { ...t, status: 'paused' as const } : t)),
          )
        } else {
          const errorMessage =
            error instanceof Error ? error.message : 'Retry failed. Please try again.'
          handleUploadError(taskId, errorMessage)
          uploaderInstances.current.delete(taskId)
        }
      }
    },
    [
      uploadTasks,
      handleUploadProgress,
      handleUploadComplete,
      handleUploadError,
      handleImageRegistered,
    ],
  )

  const handleContinueUpload = useCallback(
    async (taskId: string, imageId: number) => {
      const task = uploadTasks.find((t) => t.id === taskId)

      if (!task || !task.storeService || !task.originalFile) {
        // Fallback to file selection if no original file is saved
        if (task && task.storeService && !task.originalFile) {
          setContinuingTaskId(taskId)
          setContinuingImageId(imageId)
          setContinuingStoreService(task.storeService)
          setExpectedFileName(task.name)
          fileInputRef.current?.click()
        }
        return
      }

      // Reset task status to starting
      setUploadTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: 'starting' as const, progress: 0 } : t)),
      )

      // Register uploader instance for this task
      uploaderInstances.current.set(taskId, imageUploaderClient)

      try {
        // Start continuation upload directly with saved file
        const result = await imageUploaderClient.continueUpload(
          task.originalFile,
          imageId,
          task.storeService,
          (progress: any) => {
            handleUploadProgress(taskId, progress)
          },
        )

        handleUploadComplete(result, taskId)
        uploaderInstances.current.delete(taskId)
      } catch (error) {
        if (error instanceof UploadPausedError) {
          // Update status to paused
          setUploadTasks((prev) =>
            prev.map((t) => (t.id === taskId ? { ...t, status: 'paused' as const } : t)),
          )
        } else {
          const errorMessage =
            error instanceof Error ? error.message : 'Continue upload failed. Please try again.'
          handleUploadError(taskId, errorMessage)
          uploaderInstances.current.delete(taskId)
        }
      }
    },
    [uploadTasks, handleUploadProgress, handleUploadComplete, handleUploadError],
  )

  const handleContinueFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (
        !file ||
        !continuingTaskId ||
        !continuingImageId ||
        !continuingStoreService ||
        !expectedFileName
      )
        return

      const selectedFileName = file.name.toLowerCase()

      // Validate filename matches expected filename
      if (selectedFileName !== expectedFileName) {
        message.error(`Please select a file named "${expectedFileName}"`)
        // Clear file input
        event.target.value = ''
        return
      }

      // Remove the old pending task
      setUploadTasks((prev) => prev.filter((task) => task.id !== continuingTaskId))

      const newTaskId = generateTaskId('continue')
      // Use lowercase filename for consistency
      handleUploadStart(
        newTaskId,
        selectedFileName,
        file,
        undefined,
        continuingImageId,
        continuingStoreService,
      )

      // Register uploader instance for this task
      uploaderInstances.current.set(newTaskId, imageUploaderClient)

      try {
        // Start continuation upload (skip image registration)
        const result = await imageUploaderClient.continueUpload(
          file,
          continuingImageId,
          continuingStoreService,
          (progress: any) => {
            handleUploadProgress(newTaskId, progress)
          },
        )

        handleUploadComplete(result, newTaskId)
        // Clean up uploader instance on successful completion
        uploaderInstances.current.delete(newTaskId)
      } catch (error) {
        if (error instanceof UploadPausedError) {
          // Update status to paused
          setUploadTasks((prev) =>
            prev.map((task) =>
              task.id === newTaskId ? { ...task, status: 'paused' as const } : task,
            ),
          )
          return // Keep instance for resume
        }

        const errorMessage =
          error instanceof Error ? error.message : 'Continue upload failed. Please try again.'
        handleUploadError(newTaskId, errorMessage)
        // Clean up uploader instance on error (not pause)
        uploaderInstances.current.delete(newTaskId)
      }

      // Clear continuation state
      setContinuingTaskId(null)
      setContinuingImageId(null)
      setContinuingStoreService(null)
      setExpectedFileName(null)

      // Clear file input
      event.target.value = ''
    },
    [
      continuingTaskId,
      continuingImageId,
      continuingStoreService,
      expectedFileName,
      handleUploadStart,
      handleUploadProgress,
      handleUploadComplete,
      handleUploadError,
    ],
  )

  const handleFilterChange = (value: string) => {
    const newFilter = imageTypeFilters.find((i) => i.value === value)
    if (newFilter) {
      setFilter(newFilter)
      setPage(1)
    }
  }

  const handleRestoreImage = useCallback((image: ServerImageInfo) => {
    setRestoringImage(image)
  }, [])

  const confirmRestoreImage = useCallback(
    async (image: ServerImageInfo) => {
      setRestoreLoading(true)
      try {
        const res: any = await API.resumeImage(
          { op: 1, res_id: image.resID },
          { errMsg: false },
        ).catch(() => ({ code: -1 }))
        if (res?.code === 0) {
          message.success(t('customImage.restoreSuccess', 'Restore request completed'))
          setRestoringImage(null)
          getImageList(false)
          userStore.getAccount().catch(() => {})
        } else if (res?.code === 46) {
          setRestoringImage(null)
          setExchargeModalOpen(true)
        } else {
          message.error(getErrorCodeMessage(typeof res?.code === 'number' ? res.code : -1))
        }
      } finally {
        setRestoreLoading(false)
      }
    },
    [getImageList, t, userStore],
  )

  return (
    <div className="card bg-panel-white h-full overflow-y-auto flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="h6">
            <Trans i18nKey="customImage.title">Images</Trans>
          </div>
          {!imageOnSpaceBilling && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-100 t-sm">
              <span className="text-tertiary">
                <Trans i18nKey="customImage.imagesStorage">Images Storage</Trans>
              </span>
              <span className="t-sb text-primary">{formatBytes(imagesStorageBytes) || '0 MB'}</span>
            </div>
          )}
          <UploadStatusIndicator
            uploadTasks={uploadTasks}
            imageList={imageList}
            onContinueUpload={handleContinueUpload}
            onRetryUpload={handleRetryUpload}
            onPauseUpload={handlePauseUpload}
            onResumeUpload={handleResumeUpload}
          />
        </div>
        <div className="flex items-center gap-3">
          <Button type="outline" onClick={() => setIsImportModalOpen(true)}>
            <Trans i18nKey="customImage.importBtn">Import from URL</Trans>
          </Button>
          <Link to="#">
            <Button type="secondary" onClick={() => setIsUploadModalOpen(true)}>
              <Trans i18nKey="customImage.uploadBtn">Upload Image</Trans>
            </Button>
          </Link>
        </div>
      </div>

      <Divider />

      <ButtonRadio value={filter.value} items={imageTypeFilters} onChange={handleFilterChange} />

      {(loading || billingLoading) && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && !billingLoading && displayList.length > 0 && (
        <>
          <CustomImageTable
            imageList={visibleImageList}
            uploadTasks={uploadTasks}
            onDeleteImage={handleDeleteImage}
            onContinueUpload={handleContinueUpload}
            onPauseUpload={handlePauseUpload}
            onRestoreImage={handleRestoreImage}
          />
          <Pagination
            size={PAGE_SIZE}
            total={total}
            current={page}
            className="justify-end mt-4"
            onChange={(p) => setPage(p)}
          />
        </>
      )}
      {!loading && !billingLoading && displayList.length === 0 && (
        <div className="mt-4 flex-grow">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}

      <UploadImageModal
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadStart={handleUploadStart}
        onUploadProgress={handleUploadProgress}
        onUploadComplete={handleUploadComplete}
        onUploadError={handleUploadError}
        onUploadPaused={handleUploadPaused}
        onImageRegistered={handleImageRegistered}
        uploaderInstances={uploaderInstances.current}
        imageList={imageList}
      />

      <UploadCustomizeImageModal
        open={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        imageList={imageList}
        onSuccess={() => getImageList()}
      />

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleContinueFileSelected}
      />

      <DeleteImageModal
        open={isDeleteModalOpen}
        imageId={deletingImageId}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setDeletingImageId(null)
        }}
        onConfirm={confirmDeleteImage}
      />

      <RestoreImageModal
        open={!!restoringImage}
        image={restoringImage}
        loading={restoreLoading}
        onClose={() => setRestoringImage(null)}
        onConfirm={confirmRestoreImage}
      />

      {exchargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setExchargeModalOpen(false)
            userStore.getAccount()
            getImageList(false)
          }}
          onClose={() => {
            setExchargeModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
